/**
 * WebMCP (Web Model Context Protocol) Integration
 * 
 * Exposes structured tool declarations and form annotations for AI web agents.
 * Complies with WebMCP standard for automated form discovery, validation, and tool execution.
 */

export interface WebMCPTool {
  name: string;
  description: string;
  parameters: {
    type: "object";
    properties: Record<string, { type: string; description: string; enum?: string[] }>;
    required?: string[];
  };
  execute?: (args: any) => Promise<any> | any;
}

export const WEBMCP_TOOLS: WebMCPTool[] = [
  {
    name: "search_barbershops",
    description: "Search for barbershops in Cyprus by city, neighborhood, or name.",
    parameters: {
      type: "object",
      properties: {
        query: { type: "string", description: "Search query or shop name" },
        city: { type: "string", description: "Cyprus city (nicosia, limassol, larnaca, paphos, famagusta, kyrenia)" },
      },
    },
  },
  {
    name: "claim_barbershop",
    description: "Submit an ownership verification claim for a barbershop listing.",
    parameters: {
      type: "object",
      properties: {
        shopId: { type: "string", description: "ID or slug of the barbershop" },
        ownerName: { type: "string", description: "Full name of the shop owner" },
        email: { type: "string", description: "Contact email" },
        phone: { type: "string", description: "Contact phone number" },
        role: { type: "string", description: "Role (Owner, Manager, Partner)" },
        proofType: { type: "string", description: "Document proof type (license, utility, google, social)" },
        proofDetails: { type: "string", description: "Description or reference of ownership proof" },
      },
      required: ["shopId", "ownerName", "email", "phone", "role", "proofType", "proofDetails"],
    },
  },
  {
    name: "submit_barbershop_review",
    description: "Write a review and rating for a barbershop in Cyprus.",
    parameters: {
      type: "object",
      properties: {
        shopId: { type: "string", description: "ID of the barbershop" },
        userName: { type: "string", description: "Name of the reviewer" },
        userEmail: { type: "string", description: "Email of the reviewer" },
        rating: { type: "number", description: "Rating score from 1 to 5 stars" },
        comment: { type: "string", description: "Detailed review text" },
      },
      required: ["shopId", "userName", "userEmail", "rating", "comment"],
    },
  },
  {
    name: "contact_support",
    description: "Send a inquiry or support message to the ParberKon / zebeh team.",
    parameters: {
      type: "object",
      properties: {
        name: { type: "string", description: "Full name" },
        email: { type: "string", description: "Email address" },
        message: { type: "string", description: "Message content" },
      },
      required: ["name", "email", "message"],
    },
  },
];

/**
 * Registers WebMCP tools on global navigator / window objects if supported by AI Agent browsers,
 * and attaches standard JSON-LD schema definitions to <head>.
 */
export function initWebMCP() {
  if (typeof window === "undefined") return;

  // 1. Expose to window.webmcp / navigator.modelContext
  (window as any).webmcp = {
    version: "1.0",
    tools: WEBMCP_TOOLS,
    getTools: () => WEBMCP_TOOLS,
  };

  if ((navigator as any).modelContext) {
    try {
      WEBMCP_TOOLS.forEach((tool) => {
        (navigator as any).modelContext.registerTool?.(tool);
      });
    } catch {
      // Browser API extension format fallback
    }
  }

  // 2. Inject WebMCP JSON-LD Schema definition into document head
  const schemaId = "webmcp-tools-schema";
  let scriptEl = document.getElementById(schemaId) as HTMLScriptElement;
  if (!scriptEl) {
    scriptEl = document.createElement("script");
    scriptEl.id = schemaId;
    scriptEl.type = "application/ld+json";
    document.head.appendChild(scriptEl);
  }

  scriptEl.textContent = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "WebAPI",
    "name": "ParberKon WebMCP Tools",
    "description": "Registered WebMCP tools for AI agent interaction with ParberKon Cyprus Barbershop Directory",
    "hasOperation": WEBMCP_TOOLS.map((t) => ({
      "@type": "Operation",
      "name": t.name,
      "description": t.description,
      "potentialAction": {
        "@type": "Action",
        "name": t.name,
        "description": t.description,
      },
    })),
  });
}
